import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Memoria di navigazione della dashboard.
 *
 * 1. Ripristina la posizione di scroll quando si torna indietro con la freccia
 *    del browser o si ricarica la pagina (le SPA la perdono perché il contenuto
 *    arriva dopo il primo render: qui riproviamo finché la pagina non è alta
 *    abbastanza).
 * 2. Porta in cima quando si apre una pagina nuova (PUSH).
 * 3. Ricorda l'ultima rotta visitata, così aprendo la app dalla radice si
 *    riparte da dove si era arrivati.
 */

const SCROLL_KEY = 'bymarke:scroll-positions';
export const LAST_ROUTE_KEY = 'bymarke:last-route';

// Per quanto insistiamo a riposizionare lo scroll mentre i dati arrivano.
const RESTORE_TIMEOUT_MS = 2500;
// Quante posizioni teniamo in memoria prima di scartare le più vecchie.
const MAX_ENTRIES = 80;

type Entry = { y: number; t: number };
type Positions = Record<string, Entry>;

function readPositions(): Positions {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY);
    return raw ? (JSON.parse(raw) as Positions) : {};
  } catch {
    return {};
  }
}

function writePositions(positions: Positions) {
  try {
    const keys = Object.keys(positions);
    if (keys.length > MAX_ENTRIES) {
      keys
        .sort((a, b) => positions[a].t - positions[b].t)
        .slice(0, keys.length - MAX_ENTRIES)
        .forEach((key) => delete positions[key]);
    }
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(positions));
  } catch {
    /* storage pieno o non disponibile: la navigazione funziona lo stesso */
  }
}

/** Rotte che non ha senso riproporre all'apertura della app. */
function isRestorableRoute(pathname: string) {
  return pathname !== '/' && !pathname.startsWith('/login');
}

/** Ultima rotta visitata (path + query), se ancora sensata. */
export function getLastRoute(): string | null {
  try {
    const value = localStorage.getItem(LAST_ROUTE_KEY);
    if (!value || !value.startsWith('/')) return null;
    return isRestorableRoute(value.split('?')[0]) ? value : null;
  } catch {
    return null;
  }
}

export function useNavigationMemory() {
  const location = useLocation();
  const navigationType = useNavigationType();

  const positionsRef = useRef<Positions>(readPositions());
  const keyRef = useRef<string>('');
  const pathRef = useRef<string>('');
  const isFirstRef = useRef(true);
  const cancelRestoreRef = useRef<(() => void) | null>(null);

  // Lo gestiamo noi: il ripristino automatico del browser, su una SPA, agisce
  // prima che i dati siano caricati e finisce per non fare nulla.
  useEffect(() => {
    const previous = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    return () => {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = previous;
    };
  }, []);

  // La posizione va fissata PRIMA che il router cambi pagina: quando React ha
  // già sostituito il contenuto il browser ha azzerato lo scroll e leggerlo non
  // servirebbe più a nulla. pushState/replaceState/popstate sono i momenti
  // giusti, tutti sincroni rispetto alla navigazione.
  useEffect(() => {
    const salvaPosizioneCorrente = () => {
      const entry = { y: window.scrollY, t: Date.now() };
      if (keyRef.current) positionsRef.current[`key:${keyRef.current}`] = entry;
      if (pathRef.current) positionsRef.current[`path:${pathRef.current}`] = entry;
      writePositions(positionsRef.current);
    };

    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    window.history.pushState = function pushState(this: History, ...args: Parameters<History['pushState']>) {
      salvaPosizioneCorrente();
      return originalPush.apply(this, args);
    };
    window.history.replaceState = function replaceState(this: History, ...args: Parameters<History['replaceState']>) {
      salvaPosizioneCorrente();
      return originalReplace.apply(this, args);
    };
    window.addEventListener('popstate', salvaPosizioneCorrente);

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
      window.removeEventListener('popstate', salvaPosizioneCorrente);
    };
  }, []);

  // Registra in continuazione la posizione corrente (per chiave di cronologia e
  // per URL: la prima serve al tasto indietro, la seconda al reload).
  useEffect(() => {
    let frame = 0;
    const record = () => {
      frame = 0;
      const entry = { y: window.scrollY, t: Date.now() };
      if (keyRef.current) positionsRef.current[`key:${keyRef.current}`] = entry;
      if (pathRef.current) positionsRef.current[`path:${pathRef.current}`] = entry;
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(record);
    };
    const persist = () => {
      record();
      writePositions(positionsRef.current);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') persist();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', persist);
    window.addEventListener('beforeunload', persist);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      persist();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', persist);
      window.removeEventListener('beforeunload', persist);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useLayoutEffect(() => {
    // Un eventuale ripristino ancora in corso non deve interferire con la
    // navigazione appena avvenuta.
    cancelRestoreRef.current?.();
    cancelRestoreRef.current = null;

    const isFirst = isFirstRef.current;
    isFirstRef.current = false;

    const historyKey = location.key || 'default';
    const path = `${location.pathname}${location.search}`;

    keyRef.current = historyKey;
    pathRef.current = path;

    try {
      if (isRestorableRoute(location.pathname)) localStorage.setItem(LAST_ROUTE_KEY, path);
    } catch {
      /* ignora: la memoria dell'ultima rotta è un extra */
    }

    // Al reload la chiave di cronologia può cambiare: in quel caso ci basiamo
    // sull'URL, che è identico.
    const saved =
      positionsRef.current[`key:${historyKey}`] ??
      (isFirst ? positionsRef.current[`path:${path}`] : undefined);

    const isBackOrReload = isFirst || navigationType === 'POP';

    if (isBackOrReload && saved && saved.y > 0) {
      cancelRestoreRef.current = restoreScroll(saved.y);
      return;
    }

    // Pagina nuova: si parte dall'alto. Le navigazioni "replace" (filtri,
    // cambi di stato interni alla pagina) non toccano lo scroll.
    if (!isFirst && navigationType === 'PUSH') window.scrollTo(0, 0);
  }, [location.key, location.pathname, location.search, navigationType]);

  useEffect(() => () => cancelRestoreRef.current?.(), []);
}

/**
 * Riporta la finestra a `target` insistendo finché il contenuto caricato non
 * rende la pagina abbastanza alta. Si ferma appena l'utente scrolla da solo.
 */
function restoreScroll(target: number): () => void {
  let cancelled = false;
  let frame = 0;
  const started = Date.now();

  const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    if (frame) cancelAnimationFrame(frame);
    events.forEach((event) => window.removeEventListener(event, stop));
  };

  events.forEach((event) => window.addEventListener(event, stop, { passive: true }));

  const step = () => {
    if (cancelled) return;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo(0, Math.min(target, maxScroll));
    if (Math.abs(window.scrollY - target) < 2 || Date.now() - started > RESTORE_TIMEOUT_MS) {
      stop();
      return;
    }
    frame = requestAnimationFrame(step);
  };

  frame = requestAnimationFrame(step);
  return stop;
}

export default useNavigationMemory;
